import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ACCOUNT_TYPE_LABELS, CURRENCIES, curSym } from "../lib/format";
import { Bank, Wallet, Money, CreditCard, CheckCircle } from "@phosphor-icons/react";

const TYPE_ICONS = { bank: Bank, cash: Money, upi_wallet: Wallet, credit_card: CreditCard };

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("create");
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState(user?.name || "");
  const [inviteCode, setInviteCode] = useState("");
  const [currency, setCurrencyChoice] = useState("INR");
  const [busy, setBusy] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [acc, setAcc] = useState({ name: "", account_type: "bank", opening_balance: "" });

  const createHousehold = async () => {
    if (!name.trim()) return toast.error("Give your household a name");
    setBusy(true);
    try {
      await api.post("/households", { name: name.trim(), display_name: displayName.trim() || undefined, default_currency: currency });
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create household");
    } finally {
      setBusy(false);
    }
  };

  const join = async () => {
    if (!inviteCode.trim()) return toast.error("Enter the invite code");
    setBusy(true);
    try {
      await api.post("/households/join", { invite_code: inviteCode.trim(), display_name: displayName.trim() || undefined });
      await refresh();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not join household");
    } finally {
      setBusy(false);
    }
  };

  const addAccount = async () => {
    if (!acc.name.trim()) return toast.error("Account name required");
    try {
      const { data } = await api.post("/accounts", { ...acc, opening_balance: parseFloat(acc.opening_balance || 0) });
      setAccounts([...accounts, data]);
      setAcc({ name: "", account_type: "bank", opening_balance: "" });
      toast.success("Account added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add account");
    }
  };

  const finish = async () => {
    await refresh();
    navigate("/", { replace: true });
  };

  const input = "w-full h-12 px-4 rounded-xl bg-white border border-border focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <div className="max-w-md mx-auto min-h-screen bg-background px-6 py-10">
      <h1 className="text-3xl font-heading font-bold tracking-tight">Set up your ledger</h1>
      {step === 1 && (
        <>
          <div className="mt-6 flex rounded-xl bg-secondary p-1">
            {["create", "join"].map((t) => (
              <button
                key={t}
                data-testid={`onboarding-tab-${t}`}
                onClick={() => setTab(t)}
                className={`flex-1 h-10 rounded-lg text-sm font-medium ${tab === t ? "bg-white shadow-sm" : "text-muted-foreground"}`}
              >
                {t === "create" ? "Create household" : "Join with code"}
              </button>
            ))}
          </div>
          <div className="mt-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Your display name</label>
              <input data-testid="onboarding-display-name" className={input + " mt-1"} value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Priya" />
            </div>
            {tab === "create" ? (
              <>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Household name</label>
                  <input data-testid="onboarding-household-name" className={input + " mt-1"} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sharma Family" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Currency</label>
                  <select data-testid="onboarding-currency" className={input + " mt-1"} value={currency} onChange={(e) => setCurrencyChoice(e.target.value)}>
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <p className="text-xs text-muted-foreground">Currency: {currency} · Timezone: Asia/Kolkata · FY starts April. Sample categories will be added for you.</p>
                <button data-testid="onboarding-create-btn" disabled={busy} onClick={createHousehold} className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-medium active:scale-[0.98] transition-transform disabled:opacity-60">
                  {busy ? "Creating…" : "Create household"}
                </button>
              </>
            ) : (
              <>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Invite code</label>
                  <input data-testid="onboarding-invite-code" className={input + " mt-1 uppercase tracking-widest"} value={inviteCode} onChange={(e) => setInviteCode(e.target.value.toUpperCase())} placeholder="6-character code" maxLength={6} />
                </div>
                <p className="text-xs text-muted-foreground">Ask the household admin for the invite code (shown under More → Members).</p>
                <button data-testid="onboarding-join-btn" disabled={busy} onClick={join} className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-medium active:scale-[0.98] transition-transform disabled:opacity-60">
                  {busy ? "Joining…" : "Join household"}
                </button>
              </>
            )}
          </div>
        </>
      )}
      {step === 2 && (
        <div className="mt-6 space-y-5">
          <p className="text-sm text-muted-foreground">Add your bank, cash, UPI and credit-card accounts. You can always add more later.</p>
          {accounts.map((a) => {
            const Icon = TYPE_ICONS[a.account_type] || Bank;
            return (
              <div key={a.id} className="flex items-center gap-3 bg-white border border-border rounded-xl px-4 py-3">
                <Icon size={20} className="text-muted-foreground" />
                <span className="flex-1 text-sm font-medium">{a.name}</span>
                <CheckCircle size={18} className="text-income" weight="fill" />
              </div>
            );
          })}
          <div className="bg-white border border-border rounded-2xl p-4 space-y-3">
            <input data-testid="onboarding-account-name" className="w-full h-12 px-4 rounded-xl bg-background border border-border" placeholder="Account name (e.g. HDFC Savings)" value={acc.name} onChange={(e) => setAcc({ ...acc, name: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(ACCOUNT_TYPE_LABELS).map(([k, v]) => (
                <button key={k} data-testid={`onboarding-acctype-${k}`} onClick={() => setAcc({ ...acc, account_type: k })} className={`h-11 rounded-xl text-sm border ${acc.account_type === k ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border"}`}>
                  {v}
                </button>
              ))}
            </div>
            <input data-testid="onboarding-opening-balance" type="number" inputMode="decimal" className="w-full h-12 px-4 rounded-xl bg-background border border-border" placeholder={`Opening balance (${curSym()})`} value={acc.opening_balance} onChange={(e) => setAcc({ ...acc, opening_balance: e.target.value })} />
            <button data-testid="onboarding-add-account-btn" onClick={addAccount} className="w-full h-11 rounded-xl border border-primary text-sm font-medium active:scale-[0.98] transition-transform">
              + Add account
            </button>
          </div>
          <button data-testid="onboarding-finish-btn" onClick={finish} className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-medium active:scale-[0.98] transition-transform">
            {accounts.length ? "Go to dashboard" : "Skip for now"}
          </button>
        </div>
      )}
    </div>
  );
}
